'use client';

import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { TriviaResultStats } from './components/TriviaResultStats';
import { useTriviaBg } from './hooks/useBgTriviaResult';
import { useTriviaResults } from './hooks/useTriviaResults';

import triviaMobileCover from '@/assets/trivia-bg--no-text-mob.jpg';
import { Button, Loader } from '@/components';
import type { ResponseDashTriviaInit } from '@/types/dashboard';

// Desktop Props
export interface TriviaResultsPropsDesktop {
  triviaData?: ResponseDashTriviaInit;
  showStats?: boolean;
  comeBackTo?: () => void;
  handleShowStats?: () => void;
}

// Mobile Props
export interface TriviaResultsProps {
  triviaData?: ResponseDashTriviaInit;
  totalStats?: boolean;
  showStats?: boolean;
  setTotalStats: (value: boolean) => void;
  comeBackTo?: () => void;
  handleShowStats?: () => void;
}

// Unified Props
interface TriviaResultProps {
  triviaData?: ResponseDashTriviaInit;
  isMobile?: boolean;
  // Desktop specific
  showStats?: boolean;
  comeBackTo?: () => void;
  handleShowStats?: () => void;
  // Mobile specific
  totalStats?: boolean;
  setTotalStats?: (value: boolean) => void;
}

export const TriviaResult = ({
  triviaData,
  isMobile = false,
  // Desktop props
  showStats,
  comeBackTo,
  handleShowStats,
  // Mobile props
  totalStats: totalStatsProp,
  setTotalStats,
}: TriviaResultProps) => {
  // Desktop state management
  const [totalStats, setTotalStatsLocal] = useState(showStats);

  useEffect(() => {
    if (!isMobile) {
      setTotalStatsLocal(showStats);
    }
  }, [showStats, isMobile]);

  // Use mobile prop or desktop state
  const currentTotalStats = isMobile ? totalStatsProp : totalStats;

  const { isWinner, triviaTime, ResultMessage, title } = useTriviaResults({
    triviaData,
    totalStats: currentTotalStats,
    isMobile,
  });

  const bgResult = useTriviaBg(isMobile, isWinner ? 'winner' : 'loser');

  const handleStatsToggle = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('[TriviaResult] handleStatsToggle called:', {
      currentTotalStats,
      isMobile,
      hasHandleShowStats: !!handleShowStats,
      hasComeBackTo: !!comeBackTo,
    });

    if (isMobile) {
      // Mobile: Toggle between regular results and total stats, or go back using comeBackTo
      if (currentTotalStats && comeBackTo) {
        // When in totalStats mode, "volver" should use comeBackTo
        // eslint-disable-next-line no-console
        console.log('[TriviaResult] Mobile - Going back via comeBackTo');
        comeBackTo();
      } else if (currentTotalStats) {
        // Fallback: just toggle off totalStats
        // eslint-disable-next-line no-console
        console.log('[TriviaResult] Mobile - Toggling off totalStats');
        setTotalStats?.(false);
      } else {
        // Show total stats - use handleShowStats to properly track previous state
        if (handleShowStats) {
          // eslint-disable-next-line no-console
          console.log('[TriviaResult] Mobile - Calling handleShowStats');
          handleShowStats();
        } else {
          // eslint-disable-next-line no-console
          console.log(
            '[TriviaResult] Mobile - Fallback: setting totalStats true'
          );
          setTotalStats?.(true);
        }
      }
    } else {
      // Desktop: Similar logic
      if (currentTotalStats && comeBackTo) {
        // eslint-disable-next-line no-console
        console.log('[TriviaResult] Desktop - Going back via comeBackTo');
        comeBackTo();
      } else if (!currentTotalStats && handleShowStats) {
        // Show total stats - use handleShowStats to properly track previous state
        // eslint-disable-next-line no-console
        console.log('[TriviaResult] Desktop - Calling handleShowStats');
        handleShowStats();
      } else {
        // eslint-disable-next-line no-console
        console.log('[TriviaResult] Desktop - Toggling local state');
        setTotalStatsLocal(!currentTotalStats);
      }
    }
  }, [comeBackTo, currentTotalStats, handleShowStats, isMobile, setTotalStats]);

  const RenderMobileHeader = useCallback(() => {
    if (currentTotalStats) return null;

    return (
      <div className="flex flex-col relative mb-5">
        <img
          alt="Trivia"
          className="w-full h-full object-cover"
          loading="lazy"
          src={triviaMobileCover}
        />
        <div className="absolute px-5 flex items-center w-full justify-center h-full tracking-[2px] uppercase">
          <div className="text-white font-bold font-bebas">
            <p className="text-[30px] mb-2">
              {triviaData?.trv?.nombreCategoria}
            </p>
            <span className="text-[40px] uppercase">
              {triviaData?.trv?.nombre}
            </span>
          </div>
        </div>
      </div>
    );
  }, [
    currentTotalStats,
    triviaData?.trv?.nombre,
    triviaData?.trv?.nombreCategoria,
  ]);

  const RenderMobileResultImage = useCallback(() => {
    if (currentTotalStats) return null;

    return (
      <div className="flex flex-col relative px-5 mt-5">
        <div className="lg:rounded-l-lg rounded-t-lg w-full h-32 bg-gray-200 overflow-hidden">
          {bgResult && (
            <img
              alt="Trivia Gran DT"
              className="w-full h-full object-cover object-center"
              loading="lazy"
              src={bgResult}
            />
          )}
        </div>
        <div className="absolute flex items-center w-full justify-center h-full tracking-[2px] text-white font-bold font-bebas text-[33px] leading-8">
          {isWinner ? (
            <p className="relative -left-[14px]">
              {triviaData?.msjGanador ?? (
                <>
                  ¡Felicitaciones! <br /> Ganaste una medalla
                </>
              )}
            </p>
          ) : (
            <p className="relative -left-[14px] text-[31px] leading-7">
              Lamentablemente,
              <br />
              no ganaste una medalla
            </p>
          )}
        </div>
      </div>
    );
  }, [bgResult, currentTotalStats, isWinner, triviaData?.msjGanador]);

  const RenderTitle = useCallback(() => {
    if (isMobile) {
      return (
        <div className={`px-5 ${currentTotalStats ? 'pt-5' : 'pt-0'}`}>
          <span className="text-xl font-bold">{title}</span>
        </div>
      );
    } else {
      return <h2 className="text-xl font-bold mx-auto mt-10">{title}</h2>;
    }
  }, [currentTotalStats, isMobile, title]);

  const RenderStatsButton = useCallback(() => {
    if (!triviaData?.resultadoRanking) return null;

    return (
      <div
        className={`flex items-center mx-auto gap-x-2 mb-5 ${
          isMobile ? 'w-full justify-center' : ''
        }`}
      >
        <Button
          className="mt-4 flex font-bold no-underline mx-auto items-center space-x-2 hover:text-gdt-blue"
          type="button"
          variant="text"
          onClick={handleStatsToggle}
        >
          {currentTotalStats && (
            <ArrowLeftIcon className="stroke-gdt-blue self-end" size={20} />
          )}
          <span>
            {currentTotalStats ? 'Volver' : 'Ver estadísticas totales'}
          </span>
          {!currentTotalStats && (
            <ArrowRightIcon className="stroke-gdt-blue self-end" size={22} />
          )}
        </Button>
      </div>
    );
  }, [
    currentTotalStats,
    handleStatsToggle,
    isMobile,
    triviaData?.resultadoRanking,
  ]);

  if (isMobile) {
    return (
      <>
        <Loader isVisible={!triviaData} />
        <RenderMobileHeader />
        <RenderTitle />
        <RenderMobileResultImage />
        <TriviaResultStats
          isMobile={true}
          isWinner={isWinner}
          totalStats={currentTotalStats}
          triviaData={triviaData}
          triviaTime={triviaTime}
        />
        <div className="mt-4 mx-5 flex justify-center">
          <ResultMessage />
        </div>
        <RenderStatsButton />
      </>
    );
  }

  return (
    <>
      <Loader isVisible={!triviaData} />
      <div className="pt-9 px-10 flex flex-col gap-y-6 bg-white rounded-r-lg h-full">
        <RenderTitle />
        <TriviaResultStats
          isMobile={false}
          isWinner={isWinner}
          totalStats={currentTotalStats}
          triviaData={triviaData}
          triviaTime={triviaTime}
        />
        <div className="flex justify-center">
          <ResultMessage />
        </div>
        <RenderStatsButton />
      </div>
    </>
  );
};

// Componentes de conveniencia para mantener compatibilidad
export const TriviaResultDesktop = (props: TriviaResultsPropsDesktop) => (
  <TriviaResult {...props} isMobile={false} />
);

export const TriviaResultMobile = (props: TriviaResultsProps) => (
  <TriviaResult {...props} isMobile={true} />
);

export default TriviaResult;
